import {
  EnrolledStudentCourseUserActivityDTO,
  EnrolledStudentDTO,
  LearningPlanEnrollmentRequestDTO,
  LearningPlanInstanceDetailDTO,
  LearningPlanUnenrollmentRequestDTO,
  Phase,
  SendReminderRequestDTO,
  Status,
} from '../models/LearningPlanDTO';
import {
  LearningPlanEnrolledStudentUIModel,
  LearningPlanEnrolledStudentWithSyncStatusUIModel,
} from '../models/LearningPlanEnrolledStudentUIModel';
import {
  LearningPlanInstanceDetailUiModel,
  PhaseUiModel,
} from '../models/LearningPlanInstanceDetailUIModel';

export function toLearningPlanEnrollmentRequestDTO(
  students: Array<{ id: string }>,
): LearningPlanEnrollmentRequestDTO {
  const learningPlanEnrollmentRequestDTO =
    new LearningPlanEnrollmentRequestDTO();
  learningPlanEnrollmentRequestDTO.students = students;
  return learningPlanEnrollmentRequestDTO;
}

export function toLearningPlanUnenrollmentRequestDTO(
  students: LearningPlanEnrolledStudentUIModel[],
): LearningPlanUnenrollmentRequestDTO {
  const learningPlanUnenrollmentRequestDTO =
    new LearningPlanUnenrollmentRequestDTO();
  learningPlanUnenrollmentRequestDTO.students = students.map((student) => ({
    id: student.id,
  }));
  return learningPlanUnenrollmentRequestDTO;
}

export function toLearningPlanEnrolledStudentUIModel(
  students: EnrolledStudentDTO[],
  phases?: PhaseUiModel[],
): LearningPlanEnrolledStudentWithSyncStatusUIModel[] {
  return students.map((student) => {
    const username = `${student.firstName} ${student.lastName}`;
    const { email, id, syncStatus } = student;

    // Try to get course name from API response first
    let courseName = student.phase?.courseInstance.name ?? '';

    // If not from API, find course name from phases data
    if (!courseName && phases && student.phase?.courseInstance.id) {
      for (const phase of phases) {
        const courseInstance = phase.courseInstances.find(
          (ci) => ci.id === student.phase?.courseInstance.id,
        );
        if (courseInstance) {
          courseName = courseInstance.name;
          break;
        }
      }
    }

    return {
      id,
      username,
      email,
      syncStatus,
      currentPhase: student.phase?.name ?? '-',
      status: student.phase?.courseInstance.status ?? 'NotStarted',
      courseInstanceId: student.phase?.courseInstance.id ?? '',
      courseName,
    };
  });
}

const PROGRESS_EVENT_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})/;

function toCompletionDate(courseInstance?: {
  status: Status;
  timestamp: string;
}): string | undefined {
  if (courseInstance?.status !== Status.COMPLETED) {
    return undefined;
  }
  const eventDate = PROGRESS_EVENT_DATE_PATTERN.exec(courseInstance.timestamp);
  return eventDate
    ? `${eventDate[3]}-${eventDate[2]}-${eventDate[1]}`
    : undefined;
}

export function enrolledStudentCourseUserActivityDtoToLearningPlanEnrolledStudentUIModel(
  students: EnrolledStudentCourseUserActivityDTO[],
  phases?: PhaseUiModel[],
): LearningPlanEnrolledStudentUIModel[] {
  return students.map((student) => {
    const username = `${student.firstName} ${student.lastName}`;

    let courseName = '';

    if (phases && student.phase?.courseInstance.id) {
      for (const phase of phases) {
        const courseInstance = phase.courseInstances.find(
          (ci) => ci.id === student.phase?.courseInstance.id,
        );
        if (courseInstance) {
          courseName = courseInstance.name;
          break;
        }
      }
    }

    return {
      id: student.id,
      username: username,
      email: student.email,
      currentPhase: student.phase?.name ?? '-',
      status: student.phase?.courseInstance.status ?? 'NotStarted',
      courseInstanceId: student.phase?.courseInstance.id ?? '',
      courseName,
      completedOn: toCompletionDate(student.phase?.courseInstance),
    };
  });
}

export function toSendReminderRequestDTO(
  students: LearningPlanEnrolledStudentUIModel[],
): SendReminderRequestDTO {
  const reminderRequestDTO = new SendReminderRequestDTO();
  reminderRequestDTO.students = students.map((student) => ({
    id: student.id,
    courseInstanceId: student.courseInstanceId,
  }));
  return reminderRequestDTO;
}

function orderPhases(phases: Phase[]): Phase[] {
  const startPhase = phases.find((phase) => phase.start);
  if (!startPhase) {
    throw new Error('No starting phase found');
  }

  const orderedPhases: Phase[] = [];
  const visitedPhases: Set<string> = new Set();
  let currentPhase: Phase | undefined = startPhase;

  while (currentPhase) {
    if (visitedPhases.has(currentPhase.id)) {
      break;
    }

    orderedPhases.push(currentPhase);
    visitedPhases.add(currentPhase.id);

    let nextPhase = undefined;
    for (const phase of phases) {
      if (phase.id === currentPhase.nextPhaseId) {
        nextPhase = phase;
        break;
      }
    }
    currentPhase = nextPhase;
  }

  return orderedPhases;
}

export function toLearningPlanInstanceDetailUiModel(
  learningPlanInstanceDto: LearningPlanInstanceDetailDTO,
): LearningPlanInstanceDetailUiModel {
  const orderedPhases = orderPhases(learningPlanInstanceDto.phases);

  return {
    id: learningPlanInstanceDto.id,
    name: learningPlanInstanceDto.name,
    createdAt: learningPlanInstanceDto.createdAt,
    startAt: learningPlanInstanceDto.startAt,
    numberOfEnrolledStudents: learningPlanInstanceDto.numberOfEnrolledStudents,
    numberOfEnrolledStudentsLearningPlanStarted:
      learningPlanInstanceDto.numberOfEnrolledStudentsLearningPlanStarted,
    numberOfCompletedStudents:
      learningPlanInstanceDto.numberOfCompletedStudents,
    orderedPhases: orderedPhases,
    topicArea: learningPlanInstanceDto.topicArea,
    status: learningPlanInstanceDto.status,
    periodPickerOptions: learningPlanInstanceDto.periodPickerOptions,
    canToggleRepetition: learningPlanInstanceDto.canToggleRepetition,
    trainingPhaseType: learningPlanInstanceDto.trainingPhaseType,
  };
}
